import React from "react";
//#region types
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { StyleEnums } from "models/enums";
import { TimeReportingDetailsViewProps } from "models/propTypes/timeReportingPropTypes";
import { TimeReportingFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { DateRange } from "models/formTypes/util";
//#endregion
//#region components
import { ViewPanel, FormValidity, PageLayouts, EntityTitle, Skeletons, useFloatingMessages } from "components/common";
import { MonthlyTimeReport } from "models/formTypes/timeReporting";
import MonthBannerContainer from "./bannerContainer";
import MonthlyFormContainer from "./forms/monthlyFormContainer";
import MonthlyViewFooter from "./footer";
import MonthSelector from "./monthSelector";
import MonthSummary from "../common/monthSummary";
import { Grid } from "@mui/material";
import { useTimeReportingService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import TimeReportHelperInstance from "components/timeReporting/timeReportHelper";

const TimeReportingMonthDetailsView: React.FC<TimeReportingDetailsViewProps> = (props) => {
  const timeReportingService = useTimeReportingService();
  const formRef = React.useRef<FormHandles>(null);

  const { enqueueMessage } = useFloatingMessages();

  const [currentMetaData, setCurrentMetaData] = React.useState<ApiModel.TimeReportMetaDataWithPeriods>(props.metaData);
  const [currentMonth, setCurrentMonth] = React.useState<MonthlyTimeReport>();
  const [currentMonthLocks, setCurrentMonthLocks] = React.useState<any>();
  const [currentMonthDateRange, setCurrentMonthkDateRange] = React.useState<DateRange>();
  const [monthStatus, setMonthStatus] = React.useState<ApiModel.TimeReportStatus>();
  const [loading, setLoading] = React.useState(false);
  const [monthWeeks, setMonthWeeks] = React.useState<TimeReportingFormTypes.MonthWeek[]>([]);
  const [compHours, setCompHours] = React.useState<number>(0);

  const calculateWeekHours = (
    weekSegment: ApiModel.WeekSegment,
    week: ApiModel.Week,
    rows: TimeReportingFormTypes.MonthlyTimeReportRow[],
    weeksInMonth: TimeReportingFormTypes.MonthWeek[],
    headerRow: TimeReportingFormTypes.MonthlyTimeReportRow,
    locks: any
  ) => {
    const start = new Date(weekSegment.weekSegmentStart);
    const end = new Date(weekSegment.weekSegmentEnd);

    const filteredTimeReports =
      week.timeReports?.filter((day) => start <= new Date(day.timeLogDate) && new Date(day.timeLogDate) <= end) ?? [];
    const weekName = `${start.getDate()}-${end.getDate()}`;
    weeksInMonth.push({ weekNumber: week.weekNumber, displayText: weekName } as TimeReportingFormTypes.MonthWeek);
    let weekLock: boolean | undefined = undefined;

    filteredTimeReports.forEach((day) => {
      if (weekLock === undefined) {
        weekLock = day.isLocked || day.timeReportStatus !== ApiModel.TimeReportStatus.Open;
      } else {
        weekLock = weekLock && (day.isLocked || day.timeReportStatus !== ApiModel.TimeReportStatus.Open);
      }

      (headerRow as any)[weekName] = ((headerRow as any)[weekName] ?? 0) + day.totalHours;
      locks[weekName] = weekLock;

      TimeReportHelperInstance.calculteProjectHours(weekName, rows, day.projectTimes);
      TimeReportHelperInstance.calculateDeviationHours(weekName, rows, day.deviations);
    });
  };

  const getMonthStatus = async (employeeId: number, start: Date, end: Date) => {
    await timeReportingService
      .getDateRangeStatus(employeeId, start, end)
      .then((res) => setMonthStatus(res))
      .catch((errors) => {
        if (
          errors?.codes &&
          errors.codes.length === 1 &&
          errors.codes[0] === TranslationKeys.Errors.TimeReportsNotFound
        ) {
        } else {
          enqueueMessage(
            LocalizationHelperInstance.getFormattedMessage(errors.codes.join(", ")),
            StyleEnums.DisplayType.Danger
          );
        }
        setMonthStatus(ApiModel.TimeReportStatus.Open);
      });
  };

  const getCompTime = async (employeeId: number, firstDateOfMonth: Date) => {
    await timeReportingService
      .getCompTime(employeeId, firstDateOfMonth.getFullYear(), firstDateOfMonth.getMonth())
      .then((res) => {
        setCompHours(res.suggestedCompensationTime + res.totalExistingCompTime);
      });
  };

  const manipulateData = async (metaData: ApiModel.TimeReportMetaDataWithPeriods) => {
    setCurrentMetaData(metaData);

    if (metaData.weeks) {
      const monthRows: TimeReportingFormTypes.MonthlyTimeReportRow[] = [];
      const allWeeks: TimeReportingFormTypes.MonthWeek[] = [];
      const headerRow = {
        name: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Total),
      } as any;
      const monthLocks = {} as any;

      const secondWeekFirstDate = new Date(metaData.weeks[1].weekStart);
      const monthRange = {
        start: DateHelper.getFirstDayOfMonth(secondWeekFirstDate),
        end: DateHelper.getLastDayOfMonth(secondWeekFirstDate),
      };
      await getMonthStatus(metaData.employeeId, monthRange.start, monthRange.end);
      await getCompTime(metaData.employeeId, monthRange.start);

      metaData.weeks.forEach((week, index) => {
        if (week.weekSegments) {
          if (week.weekSegments.length > 1) {
            const segment = index === 0 ? week.weekSegments[1] : week.weekSegments[0];
            calculateWeekHours(segment, week, monthRows, allWeeks, headerRow, monthLocks);
          } else {
            calculateWeekHours(week.weekSegments[0], week, monthRows, allWeeks, headerRow, monthLocks);
          }
        }
      });
      setMonthWeeks(allWeeks);
      setCurrentMonth({ rows: monthRows.sort((a, b) => Number(a.type) - Number(b.type)), headerRow: headerRow });

      setCurrentMonthkDateRange({
        start: DateHelper.getFirstDayOfMonth(new Date(metaData.weeks[1].weekStart)),
        end: DateHelper.getLastDayOfMonth(new Date(metaData.weeks[1].weekStart)),
      });
      setCurrentMonthLocks(monthLocks);
    }
  };

  React.useEffect(() => {
    setLoading(true);
    manipulateData(props.metaData).then((res) => setLoading(false));
  }, [props.metaData]);

  const loadData = (newDate: Date) => {
    if (newDate && !loading) {
      setLoading(true);
      timeReportingService
        .filter(
          currentMetaData.employeeId,
          DateHelper.getFirstDayOfMonth(newDate),
          DateHelper.getLastDayOfMonth(newDate)
        )
        .then(async (res) => {
          await manipulateData(res);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const onReloadMonth = () => {
    currentMonthDateRange?.start && loadData(currentMonthDateRange?.start);
  };

  return (
    <FormValidity
      render={(isValid, onValidityChange) => (
        <PageLayouts.EntityViewLayout
          displayType={StyleEnums.DisplayType.Info}
          leftColumnSize="sm"
          upperLeftComponent={
            <Grid container alignItems="flex-end">
              <EntityTitle
                subTitle={currentMetaData.employeeName ?? ""}
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.TimeReporting)}
              />
            </Grid>
          }
          upperRightComponent={
            <MonthSelector currentMonthDateRange={currentMonthDateRange} onMonthToggle={loadData}></MonthSelector>
          }
          lowerLeftComponent={<MonthSummary formData={currentMonth?.rows} monthWeeks={monthWeeks}></MonthSummary>}
          footer={
            <MonthlyViewFooter
              isValid={isValid && !loading}
              employeeId={currentMetaData.employeeId}
              reloadHandler={onReloadMonth}
              monthStatus={monthStatus}
              dateRange={currentMonthDateRange}
            ></MonthlyViewFooter>
          }
        >
          <>
            {loading ? (
              <Skeletons.FormSkeleton></Skeletons.FormSkeleton>
            ) : (
              <MonthBannerContainer
                monthData={currentMonth?.rows}
                monthWeeks={monthWeeks}
                dateRange={currentMonthDateRange}
                compHours={compHours}
                monthStatus={monthStatus}
              />
            )}
            {loading ? (
              <Skeletons.FormSkeleton></Skeletons.FormSkeleton>
            ) : (
              monthWeeks && (
                <ViewPanel className={clsx(cssExports.timeReportingFormContainer, cssExports.monthlyContainer)}>
                  <MonthlyFormContainer
                    ref={formRef}
                    onFormValidationChange={onValidityChange}
                    currentMetaData={currentMetaData}
                    currentMonth={currentMonth}
                    dateRange={currentMonthDateRange}
                    monthLockEntries={currentMonthLocks}
                    weeksInMonth={monthWeeks.map((mw) => mw.displayText!)}
                  />
                </ViewPanel>
              )
            )}
          </>
        </PageLayouts.EntityViewLayout>
      )}
    />
  );
};
export default TimeReportingMonthDetailsView;
